import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, addDays, addWeeks, subWeeks } from 'date-fns';
import { es } from 'date-fns/locale';

const DIAS       = ['lunes','martes','miercoles','jueves','viernes','sabado','domingo'];
const DIAS_LABEL = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

const horasEntre = (inicio, fin) => {
  if (!inicio || !fin) return 0;
  const i = inicio.slice(0,5), f = fin.slice(0,5);
  const [hi,mi] = i.split(':').map(Number);
  const [hf,mf] = f.split(':').map(Number);
  let d = (hf*60+mf)-(hi*60+mi);
  if (d<0) d+=1440;
  return Math.round(d/60*10)/10;
};

const lunesDe = (fecha) => {
  const d = new Date(fecha);
  const dia = d.getDay();
  d.setDate(d.getDate() + (dia===0 ? -6 : 1-dia));
  return format(d,'yyyy-MM-dd');
};

const colorHoras = (h) => {
  if (h>44)  return { color:'#EF4444', bg:'#FEE2E2', label:'Horas extra' };
  if (h>=42) return { color:'#10B981', bg:'#D1FAE5', label:'Completo' };
  if (h>0)   return { color:'#F59E0B', bg:'#FEF3C7', label:'Incompleto' };
  return { color:'#D1D5DB', bg:'#F9FAFB', label:'Sin programar' };
};

const estadoColor = {
  borrador:  { bg:'#F3F4F6', color:'#6B7280', label:'Borrador' },
  enviado:   { bg:'#DBEAFE', color:'#1E40AF', label:'Enviado a gerencia' },
  aprobado:  { bg:'#D1FAE5', color:'#065F46', label:'Aprobado' },
  rechazado: { bg:'#FEE2E2', color:'#991B1B', label:'Rechazado' },
};

const M = {
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
  modal:   { background:'white', borderRadius:16, width:'100%', boxShadow:'0 24px 64px rgba(0,0,0,0.3)', overflow:'hidden' },
  header:  { background:'#1a1a2e', color:'white', padding:'18px 24px' },
  footer:  { display:'flex', gap:10, padding:'16px 24px', borderTop:'1px solid #F3F4F6', justifyContent:'flex-end' },
  input:   { width:'100%', padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' },
  btnSec:  { padding:'9px 18px', border:'2px solid #E5E7EB', borderRadius:8, background:'white', cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' },
  btnPri:  { padding:'9px 20px', border:'none', borderRadius:8, background:'linear-gradient(135deg,#1a1a2e,#16213e)', color:'white', cursor:'pointer', fontSize:13, fontWeight:700 },
};

// ─── MODAL TURNOS MÚLTIPLES POR DÍA ──────────────────────
function ModalTurnosDia({ progId, empleadoNombre, dia, diaLabel, fecha, onClose, onGuardado }) {
  const [turnos, setTurnos] = useState([
    { turno_num:1, hora_inicio:'', hora_fin:'' },
    { turno_num:2, hora_inicio:'', hora_fin:'' },
    { turno_num:3, hora_inicio:'', hora_fin:'' },
  ]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    API.get(`/programacion/${progId}/turnos`)
      .then(r => {
        const porDia = r.data.filter(t => t.dia === dia);
        setTurnos(prev => prev.map(t => {
          const found = porDia.find(x => x.turno_num === t.turno_num);
          return found ? { turno_num: t.turno_num, hora_inicio: found.hora_inicio.slice(0,5), hora_fin: found.hora_fin.slice(0,5) } : t;
        }));
      }).catch(() => {});
  }, [progId, dia]);

  const setHora = (num, campo, val) => {
    setTurnos(prev => prev.map(t => t.turno_num === num ? { ...t, [campo]: val } : t));
  };

  const totalHoras = turnos.reduce((a, t) => a + horasEntre(t.hora_inicio, t.hora_fin), 0);

  const guardar = async () => {
    setGuardando(true);
    try {
      for (const t of turnos) {
        if (t.hora_inicio && t.hora_fin) {
          await API.post(`/programacion/${progId}/turnos`, { dia, turno_num: t.turno_num, hora_inicio: t.hora_inicio, hora_fin: t.hora_fin });
        } else {
          await API.delete(`/programacion/${progId}/turnos`, { data: { dia, turno_num: t.turno_num } }).catch(() => {});
        }
      }
      toast.success('Turnos guardados');
      onGuardado();
      onClose();
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: 420 }}>
        <div style={M.header}>
          <div style={{ fontSize:12, opacity:0.7 }}>🕐 Turnos del día</div>
          <div style={{ fontSize:17, fontWeight:800 }}>{empleadoNombre}</div>
          <div style={{ fontSize:13, opacity:0.8, marginTop:3 }}>{diaLabel} {fecha}</div>
        </div>
        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:12 }}>
          {turnos.map((t, i) => (
            <div key={t.turno_num} style={{ background:'#F9FAFB', borderRadius:10, padding:'12px 14px' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', marginBottom:8, textTransform:'uppercase' }}>
                Turno {t.turno_num} {horasEntre(t.hora_inicio, t.hora_fin) > 0 ? `— ${horasEntre(t.hora_inicio, t.hora_fin)}h` : '(opcional)'}
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11, color:'#9CA3AF', marginBottom:3 }}>Entrada</div>
                  <input type="time" value={t.hora_inicio} onChange={e => setHora(t.turno_num,'hora_inicio',e.target.value)}
                    style={{ ...M.input, fontSize:14 }} />
                </div>
                <div style={{ color:'#D1D5DB', marginTop:14 }}>→</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11, color:'#9CA3AF', marginBottom:3 }}>Salida</div>
                  <input type="time" value={t.hora_fin} onChange={e => setHora(t.turno_num,'hora_fin',e.target.value)}
                    style={{ ...M.input, fontSize:14 }} />
                </div>
                {(t.hora_inicio || t.hora_fin) && (
                  <button onClick={() => setHora(t.turno_num,'hora_inicio','') || setHora(t.turno_num,'hora_fin','')}
                    style={{ marginTop:14, background:'none', border:'none', cursor:'pointer', color:'#EF4444', fontSize:16 }}>✕</button>
                )}
              </div>
            </div>
          ))}
          {totalHoras > 0 && (
            <div style={{ background: totalHoras > 44/7*1.2 ? '#FEE2E2' : '#F0FDF4', borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Total del día</span>
              <span style={{ fontFamily:'monospace', fontWeight:800, fontSize:16 }}>{totalHoras}h</span>
            </div>
          )}
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={M.btnPri}>
            {guardando ? '⏳...' : '💾 Guardar turnos'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL RECHAZO ───────────────────────────────────────
function ModalRechazo({ bodegaId, semanaInicio, onClose, onRechazado }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const rechazar = async () => {
    if (!motivo.trim()) { toast.error('Indica el motivo'); return; }
    setEnviando(true);
    try {
      await API.post('/programacion/rechazar', { bodega_id: bodegaId, semana_inicio: semanaInicio, motivo });
      toast.success('Horario rechazado');
      onRechazado(); onClose();
    } catch { toast.error('Error'); }
    finally { setEnviando(false); }
  };
  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth:400 }}>
        <div style={M.header}>
          <div style={{ fontSize:17, fontWeight:800 }}>✕ Rechazar horario</div>
        </div>
        <div style={{ padding:'20px 24px' }}>
          <label style={{ fontSize:12, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Motivo del rechazo *</label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
            style={{ ...M.input, marginTop:6, resize:'none' }} placeholder="Ej: Falta personal el miércoles..." />
        </div>
        <div style={M.footer}>
          <button onClick={onClose} style={M.btnSec}>Cancelar</button>
          <button onClick={rechazar} disabled={enviando} style={{ ...M.btnPri, background:'#EF4444' }}>
            {enviando ? '⏳...' : '✕ Rechazar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── GENERAR PDF HORARIO POR EMPLEADO ────────────────────
async function generarPDFEmpleado(empleado, semanaInicio, turnosPorProg, programacionBodegas, bodegas) {
  const script1 = document.createElement('script');
  script1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  document.head.appendChild(script1);
  await new Promise(r => script1.onload = r);
  const script2 = document.createElement('script');
  script2.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
  document.head.appendChild(script2);
  await new Promise(r => script2.onload = r);

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
  const lunesDate = new Date(semanaInicio + 'T12:00');
  const viernesDate = addDays(lunesDate, 6);

  doc.setFontSize(18); doc.setFont('helvetica','bold');
  doc.text('Gelatus — Horario Semanal', 14, 20);
  doc.setFontSize(12); doc.setFont('helvetica','normal');
  doc.text(`Empleado: ${empleado.nombre}`, 14, 30);
  doc.text(`Cargo: ${empleado.cargo || '—'}`, 14, 37);
  doc.text(`Semana: ${format(lunesDate,"d 'de' MMMM",{locale:es})} al ${format(viernesDate,"d 'de' MMMM yyyy",{locale:es})}`, 14, 44);
  doc.setDrawColor(200,200,200); doc.line(14, 48, 196, 48);

  const body = [];
  let totalSemana = 0;
  DIAS.forEach((dia, i) => {
    const fecha = addDays(lunesDate, i);
    const fechaStr = format(fecha, 'yyyy-MM-dd');
    const turnosDia = [];
    // Buscar en todas las bodegas
    Object.entries(programacionBodegas).forEach(([bodId, filas]) => {
      const prog = filas[empleado.id];
      if (!prog) return;
      const tBod = (turnosPorProg[prog.id] || []).filter(t => t.dia === dia);
      if (tBod.length) {
        const bodega = bodegas.find(b => String(b.id) === String(bodId));
        tBod.forEach(t => {
          const h = horasEntre(t.hora_inicio, t.hora_fin);
          turnosDia.push({ bodega: bodega?.nombre, inicio: t.hora_inicio.slice(0,5), fin: t.hora_fin.slice(0,5), horas: h });
          totalSemana += h;
        });
      }
    });
    const diaLabel = `${DIAS_LABEL[i]} ${format(fecha,'d/M')}`;
    if (turnosDia.length === 0) {
      body.push([diaLabel, 'Día libre', '—', '—', '0h']);
    } else {
      turnosDia.forEach((t, j) => {
        body.push([
          j === 0 ? diaLabel : '',
          t.bodega,
          t.inicio,
          t.fin,
          `${t.horas}h`,
        ]);
      });
    }
  });

  doc.autoTable({
    startY: 54,
    head: [['Día','Punto de venta','Entrada','Salida','Horas']],
    body,
    styles: { fontSize:10 },
    headStyles: { fillColor:[26,26,46], textColor:255 },
    alternateRowStyles: { fillColor:[248,248,248] },
    columnStyles: { 0:{fontStyle:'bold'}, 2:{halign:'center'}, 3:{halign:'center'}, 4:{halign:'center', fontStyle:'bold'} },
  });

  const y = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(13); doc.setFont('helvetica','bold');
  doc.text(`Total semana: ${totalSemana.toFixed(1)} horas`, 14, y);

  doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(150);
  doc.text(`Generado el ${format(new Date(),"d 'de' MMMM yyyy",{locale:es})}`, 14, y + 8);

  doc.save(`Horario_${empleado.nombre.replace(/ /g,'_')}_${semanaInicio}.pdf`);
}

// ─── VISTA DE COBERTURA ───────────────────────────────────
function VistCobertura({ bodegas, empleados, prog, turnos, semanaInicio }) {
  const lunesDate = new Date(semanaInicio + 'T12:00');

  // Para cada día, calcular franjas de cobertura por bodega
  const calcularFranjasDia = (diaIdx) => {
    const dia = DIAS[diaIdx];
    // Recopilar todos los puntos de cambio de todos los turnos de todas las bodegas
    const puntosSet = new Set();
    bodegas.forEach(bodega => {
      const filas = prog[bodega.id] || {};
      empleados.forEach(emp => {
        const p = filas[emp.id];
        if (!p) return;
        (turnos[p.id] || []).filter(t => t.dia === dia).forEach(t => {
          puntosSet.add(t.hora_inicio.slice(0,5));
          puntosSet.add(t.hora_fin.slice(0,5));
        });
      });
    });

    if (puntosSet.size < 2) return [];

    const puntos = Array.from(puntosSet).sort();
    const franjas = [];
    for (let i = 0; i < puntos.length - 1; i++) {
      const desde = puntos[i];
      const hasta = puntos[i + 1];
      const cobertura = {}; // bodegaId -> [nombres]
      bodegas.forEach(bodega => {
        const filas = prog[bodega.id] || {};
        const presentes = [];
        empleados.forEach(emp => {
          const p = filas[emp.id];
          if (!p) return;
          const tDia = (turnos[p.id] || []).filter(t => t.dia === dia);
          const estaPresente = tDia.some(t => {
            const ini = t.hora_inicio.slice(0,5);
            const fin = t.hora_fin.slice(0,5);
            return ini <= desde && fin >= hasta;
          });
          if (estaPresente) presentes.push(emp.nombre.split(' ')[0]); // solo primer nombre
        });
        cobertura[bodega.id] = presentes;
      });
      franjas.push({ desde, hasta, cobertura });
    }
    return franjas;
  };

  // Bodegas de rotación (las 3 de Montería) — mostrar juntas
  const bodegasVista = bodegas.slice(0, 3); // primeras 3 o todas

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {DIAS.map((dia, diaIdx) => {
        const fecha = addDays(lunesDate, diaIdx);
        const franjas = calcularFranjasDia(diaIdx);
        if (franjas.length === 0) return null;

        // Horario global del día (apertura - cierre)
        const todasHoras = franjas.flatMap(f => [f.desde, f.hasta]);
        const apertura = todasHoras[0];
        const cierre = todasHoras[todasHoras.length - 1];

        return (
          <div key={dia} style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,0.08)' }}>
            {/* Header día */}
            <div style={{ background:'#1a1a2e', padding:'10px 16px', display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ color:'white', fontWeight:800, fontSize:15 }}>
                {DIAS_LABEL[diaIdx]} {format(fecha, 'd', { locale: es })}
              </div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', fontFamily:'monospace' }}>
                {apertura} – {cierre}
              </div>
            </div>

            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:500 }}>
                <thead>
                  <tr style={{ background:'#EFF6FF' }}>
                    <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#1E40AF', width:110, borderBottom:'2px solid #BFDBFE' }}>
                      Franja
                    </th>
                    {bodegasVista.map(b => (
                      <th key={b.id} style={{ padding:'8px 12px', textAlign:'center', fontSize:11, fontWeight:700, color:'#1E40AF', borderBottom:'2px solid #BFDBFE', borderLeft:'1px solid #BFDBFE' }}>
                        {b.nombre.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {franjas.map((franja, fi) => {
                    const tieneCoberturaAlguna = bodegasVista.some(b => franja.cobertura[b.id]?.length > 0);
                    return (
                      <tr key={fi} style={{ borderBottom:'1px solid #F3F4F6', background: fi % 2 === 0 ? 'white' : '#FAFBFF' }}>
                        <td style={{ padding:'8px 12px', fontFamily:'monospace', fontWeight:700, fontSize:12, color:'#374151', whiteSpace:'nowrap' }}>
                          {franja.desde}–{franja.hasta}
                        </td>
                        {bodegasVista.map(b => {
                          const nombres = franja.cobertura[b.id] || [];
                          return (
                            <td key={b.id} style={{ padding:'7px 12px', borderLeft:'1px solid #F3F4F6', textAlign:'center' }}>
                              {nombres.length > 0 ? (
                                <div style={{ display:'flex', flexWrap:'wrap', gap:4, justifyContent:'center' }}>
                                  {nombres.map((n, ni) => (
                                    <span key={ni} style={{ fontSize:11, fontWeight:700, color:'#1E40AF', background:'#DBEAFE', padding:'2px 8px', borderRadius:20, whiteSpace:'nowrap' }}>
                                      {n}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color:'#E5E7EB', fontSize:12 }}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      }).filter(Boolean)}

      {DIAS.every((_, i) => calcularFranjasDia(i).length === 0) && (
        <div style={{ background:'white', borderRadius:14, padding:40, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📅</div>
          <div style={{ fontWeight:600 }}>Aún no hay turnos programados esta semana</div>
        </div>
      )}
    </div>
  );
}

// ─── MÓDULO PRINCIPAL ─────────────────────────────────────

// ─────────────────────────────────────────────
// VISTA EMPLEADO — Mi horario semanal
// ─────────────────────────────────────────────
function VistaHorarioEmpleado({ usuario }) {
  const hoy = new Date();
  const [semanaInicio, setSemanaInicio] = useState(lunesDe(hoy));
  const [turnos, setTurnos] = useState([]);
  const [cargando, setCargando] = useState(false);

  const DIAS = ['lunes','martes','miercoles','jueves','viernes','sabado','domingo'];
  const DIAS_LABEL = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

  useEffect(() => {
    if (!usuario?.email) return;
    setCargando(true);
    // Buscar programaciones de la semana por email del empleado
    API.get('/horario-empleado', { params: { email: usuario.email, semana_inicio: semanaInicio } })
      .then(r => setTurnos(r.data))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [semanaInicio, usuario]);

  const semanaAnterior = () => setSemanaInicio(format(addDays(new Date(semanaInicio+'T12:00'), -7), 'yyyy-MM-dd'));
  const semanaSiguiente = () => setSemanaInicio(format(addDays(new Date(semanaInicio+'T12:00'), 7), 'yyyy-MM-dd'));

  const fechasSemana = DIAS.map((_, i) => addDays(new Date(semanaInicio+'T12:00'), i));
  const hoyStr = format(hoy, 'yyyy-MM-dd');

  // Agrupar turnos por día
  const turnosPorDia = {};
  DIAS.forEach(d => { turnosPorDia[d] = []; });
  turnos.forEach(t => {
    if (turnosPorDia[t.dia]) turnosPorDia[t.dia].push(t);
  });

  const totalHoras = DIAS.reduce((total, dia) => {
    return total + turnosPorDia[dia].reduce((s, t) => s + horasEntre(t.hora_inicio, t.hora_fin), 0);
  }, 0);

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>🗓️ Mi Horario</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 13 }}>{usuario?.nombre}</p>
      </div>

      {/* Navegación semana */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', borderRadius: 12, padding: '12px 16px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <button onClick={semanaAnterior} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#374151' }}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
            {format(new Date(semanaInicio+'T12:00'), "d MMM", { locale: es })} — {format(addDays(new Date(semanaInicio+'T12:00'), 6), "d MMM yyyy", { locale: es })}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
            {totalHoras > 0 ? `${totalHoras}h programadas` : 'Sin programar'}
          </div>
        </div>
        <button onClick={semanaSiguiente} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#374151' }}>›</button>
      </div>

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DIAS.map((dia, i) => {
            const fecha = fechasSemana[i];
            const fechaStr = format(fecha, 'yyyy-MM-dd');
            const esHoy = fechaStr === hoyStr;
            const turnos_dia = turnosPorDia[dia];
            const horas = turnos_dia.reduce((s, t) => s + horasEntre(t.hora_inicio, t.hora_fin), 0);

            return (
              <div key={dia} style={{
                background: 'white', borderRadius: 12, padding: '12px 16px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                borderLeft: `4px solid ${esHoy ? '#3B4E8C' : turnos_dia.length > 0 ? '#10B981' : '#E5E7EB'}`,
                opacity: fechaStr < hoyStr ? 0.7 : 1,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: esHoy ? 800 : 600, fontSize: 14, color: esHoy ? '#3B4E8C' : '#111827' }}>
                      {DIAS_LABEL[i]} {format(fecha, 'd', { locale: es })}
                      {esHoy && <span style={{ fontSize: 10, background: '#3B4E8C', color: 'white', padding: '1px 6px', borderRadius: 10, marginLeft: 6, fontWeight: 700 }}>HOY</span>}
                    </div>
                    {turnos_dia.length > 0 ? (
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                        {turnos_dia.map((t, ti) => (
                          <span key={ti}>
                            {t.bodega_nombre && <span style={{ color: '#9CA3AF' }}>{t.bodega_nombre} · </span>}
                            {t.hora_inicio?.slice(0,5)} – {t.hora_fin?.slice(0,5)}
                            {ti < turnos_dia.length - 1 && ' / '}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 3 }}>Libre</div>
                    )}
                  </div>
                  {horas > 0 && (
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#374151' }}>{horas}h</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Horarios() {
  const { usuario } = useAuth();
  const esGerencia = usuario?.rol === 'gerencia';
  const esAdmin    = usuario?.rol === 'administracion' || esGerencia;
  const esEmpleado = usuario?.rol === 'empleado';

  if (esEmpleado) return <VistaHorarioEmpleado usuario={usuario} />;

  const hoy = new Date();
  const [semanaInicio, setSemanaInicio] = useState(lunesDe(hoy));
  const [bodegas, setBodegas]           = useState([]);
  const [empleados, setEmpleados]       = useState([]);
  const [tab, setTab]                   = useState(null);
  const [vista, setVista]               = useState('editar'); // 'editar' | 'cobertura'
  // prog[bodegaId][empleadoId] = row de programacion_semanal
  const [prog, setProg]                 = useState({});
  // turnos[progId] = [{ dia, turno_num, hora_inicio, hora_fin }]
  const [turnos, setTurnos]             = useState({});
  const [estados, setEstados]           = useState({});
  const [guardando, setGuardando]       = useState(null);
  const [modalTurno, setModalTurno]     = useState(null);
  const [modalRechazo, setModalRechazo] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(null);

  const fechasDias = DIAS.map((_, i) => {
    const d = new Date(semanaInicio + 'T12:00');
    d.setDate(d.getDate() + i);
    return format(d, 'yyyy-MM-dd');
  });

  useEffect(() => {
    Promise.all([
      API.get('/bodegas').catch(() => ({ data:[] })),
      API.get('/empleados').catch(() => ({ data:[] })),
    ]).then(([b,e]) => {
      const pvs = b.data.filter(x => x.tipo==='punto_venta'||!x.tipo);
      setBodegas(pvs);
      if (pvs.length && !tab) setTab(String(pvs[0].id));
      setEmpleados(e.data);
    });
  }, []);

  const cargar = useCallback(() => {
    if (!bodegas.length) return;
    bodegas.forEach(bodega => {
      API.get('/programacion', { params:{ bodega_id:bodega.id, semana_inicio:semanaInicio } })
        .then(async r => {
          const map = {};
          r.data.forEach(p => { map[p.empleado_id] = p; });
          setProg(prev => ({ ...prev, [bodega.id]: map }));
          // Cargar turnos de cada programación
          for (const p of r.data) {
            const t = await API.get(`/programacion/${p.id}/turnos`).catch(() => ({ data:[] }));
            setTurnos(prev => ({ ...prev, [p.id]: t.data }));
          }
          // Estado
          API.get('/programacion/estado', { params:{ bodega_id:bodega.id, semana_inicio:semanaInicio } })
            .then(s => setEstados(prev => ({ ...prev, [bodega.id]: s.data })))
            .catch(() => {});
        }).catch(() => {});
    });
  }, [bodegas, semanaInicio]);

  useEffect(() => { cargar(); }, [cargar]);

  // Asegurar fila de programación existe antes de editar turnos
  const asegurarProg = async (bodegaId, empleadoId) => {
    const existing = prog[bodegaId]?.[empleadoId];
    if (existing?.id) return existing;
    const r = await API.post('/programacion', {
      bodega_id: bodegaId, semana_inicio: semanaInicio, empleado_id: empleadoId,
      ...DIAS.reduce((acc, dia) => ({ ...acc, [`${dia}_inicio`]:null, [`${dia}_fin`]:null }), {}),
    });
    setProg(prev => ({ ...prev, [bodegaId]: { ...(prev[bodegaId]||{}), [empleadoId]: r.data } }));
    return r.data;
  };

  const abrirTurno = async (bodegaId, empleadoId, dia, diaLabel, fecha) => {
    const p = await asegurarProg(bodegaId, empleadoId);
    const emp = empleados.find(e => e.id === empleadoId);
    setModalTurno({ progId: p.id, empleadoNombre: emp?.nombre, dia, diaLabel, fecha });
  };

  // Calcular horas totales por empleado por día sumando todos los puntos
  const horasPorEmpleadoDia = (empleadoId, diaIdx) => {
    const dia = DIAS[diaIdx];
    return Object.entries(prog).reduce((total, [bodId, filas]) => {
      const p = filas[empleadoId];
      if (!p) return total;
      const tDia = (turnos[p.id] || []).filter(t => t.dia === dia);
      return total + tDia.reduce((a, t) => a + horasEntre(t.hora_inicio, t.hora_fin), 0);
    }, 0);
  };

  const horasSemanalesEmpleado = (empleadoId) =>
    DIAS.reduce((t, _, i) => t + horasPorEmpleadoDia(empleadoId, i), 0);

  // Turnos de un empleado en una bodega para un día
  const turnosDeDia = (bodegaId, empleadoId, dia) => {
    const p = prog[bodegaId]?.[empleadoId];
    if (!p) return [];
    return (turnos[p.id] || []).filter(t => t.dia === dia);
  };

  const enviarAGerencia = async (bodegaId) => {
    try {
      await API.post('/programacion/enviar', { bodega_id: bodegaId, semana_inicio: semanaInicio });
      toast.success('Horario enviado a gerencia para aprobación');
      cargar();
    } catch { toast.error('Error al enviar'); }
  };

  const aprobarHorario = async (bodegaId) => {
    try {
      await API.post('/programacion/aprobar', { bodega_id: bodegaId, semana_inicio: semanaInicio });
      toast.success('✅ Horario aprobado — turnos generados');
      cargar();
    } catch { toast.error('Error al aprobar'); }
  };

  const semanaLabel = () => {
    const inicio = new Date(semanaInicio + 'T12:00');
    const fin = addDays(inicio, 6);
    return `${format(inicio,"d 'de' MMM",{locale:es})} — ${format(fin,"d 'de' MMM yyyy",{locale:es})}`;
  };

  const empleadosParaBodega = (bodegaId) => {
    const bodega = bodegas.find(b => String(b.id) === String(bodegaId));
    if (!bodega) return [];
    return empleados;
  };

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", maxWidth:1200, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:'#111827' }}>🗓️ Horarios</h1>
          <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:14 }}>Programación semanal · aprobación por gerencia</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, background:'white', borderRadius:10, padding:'6px 12px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          <button onClick={() => setSemanaInicio(lunesDe(subWeeks(new Date(semanaInicio+'T12:00'),1)))} style={{ border:'none', background:'none', cursor:'pointer', fontSize:20, color:'#374151' }}>‹</button>
          <span style={{ fontSize:14, fontWeight:700, color:'#111827', minWidth:230, textAlign:'center' }}>{semanaLabel()}</span>
          <button onClick={() => setSemanaInicio(lunesDe(addWeeks(new Date(semanaInicio+'T12:00'),1)))} style={{ border:'none', background:'none', cursor:'pointer', fontSize:20, color:'#374151' }}>›</button>
          <button onClick={() => setSemanaInicio(lunesDe(hoy))} style={{ padding:'5px 10px', border:'1px solid #E5E7EB', borderRadius:7, background:'white', cursor:'pointer', fontSize:12, fontWeight:600, color:'#374151', marginLeft:4 }}>Hoy</button>
        </div>
        {/* Toggle editar / cobertura */}
        <div style={{ display:'flex', background:'white', borderRadius:10, padding:3, boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
          {[{ id:'editar', label:'✏️ Editar' }, { id:'cobertura', label:'👁️ Cobertura' }].map(v => (
            <button key={v.id} onClick={() => setVista(v.id)} style={{ padding:'7px 16px', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight: vista===v.id ? 700 : 500, background: vista===v.id ? '#1a1a2e' : 'transparent', color: vista===v.id ? 'white' : '#6B7280' }}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs por bodega — solo en editar */}
      {vista === 'editar' && (
        <div style={{ display:'flex', gap:4, marginBottom:20, background:'white', borderRadius:12, padding:4, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', width:'fit-content', flexWrap:'wrap' }}>
          {bodegas.map(b => {
            const est = estados[b.id]?.estado || 'borrador';
            const col = estadoColor[est];
            return (
              <button key={b.id} onClick={() => setTab(String(b.id))} style={{
                padding:'8px 16px', border:'none', borderRadius:8, cursor:'pointer', fontSize:13,
                fontWeight: tab===String(b.id) ? 700 : 500,
                background: tab===String(b.id) ? '#1a1a2e' : 'transparent',
                color: tab===String(b.id) ? 'white' : '#6B7280',
                position:'relative',
              }}>
                {b.nombre}
                <span style={{ marginLeft:6, fontSize:10, padding:'1px 6px', borderRadius:10, background: tab===String(b.id) ? 'rgba(255,255,255,0.2)' : col.bg, color: tab===String(b.id) ? 'white' : col.color, fontWeight:700 }}>
                  {col.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Vista cobertura */}
      {vista === 'cobertura' && (
        <VistCobertura
          bodegas={bodegas}
          empleados={empleados}
          prog={prog}
          turnos={turnos}
          semanaInicio={semanaInicio}
        />
      )}

      {/* Tabla del punto activo — solo en vista editar */}
      {vista === 'editar' && tab && (() => {
        const bodegaActual = bodegas.find(b => String(b.id) === tab);
        const emps = empleadosParaBodega(tab);
        const progBodega = prog[tab] || {};
        const estado = estados[tab]?.estado || 'borrador';
        const bloqueado = estado === 'aprobado';
        const puedeEnviar = esAdmin && !esGerencia && estado === 'borrador';
        const puedeAprobar = esGerencia && estado === 'enviado';
        const puedeRechazar = esGerencia && estado === 'enviado';

        return (
          <div style={{ background:'white', borderRadius:16, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden', marginBottom:24 }}>
            {/* Header bodega */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px', borderBottom:'1px solid #F3F4F6', flexWrap:'wrap', gap:10 }}>
              <div>
                <div style={{ fontWeight:800, fontSize:16 }}>🏪 {bodegaActual?.nombre}</div>
                {estado==='rechazado' && estados[tab]?.rechazo_motivo && (
                  <div style={{ fontSize:12, color:'#EF4444', marginTop:3 }}>✕ Rechazado: {estados[tab].rechazo_motivo}</div>
                )}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {puedeEnviar && (
                  <button onClick={() => enviarAGerencia(tab)} style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#3B82F6', color:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                    📤 Enviar a gerencia
                  </button>
                )}
                {puedeAprobar && (
                  <button onClick={() => aprobarHorario(tab)} style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#10B981', color:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                    ✅ Aprobar
                  </button>
                )}
                {puedeRechazar && (
                  <button onClick={() => setModalRechazo(tab)} style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#EF4444', color:'white', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                    ✕ Rechazar
                  </button>
                )}
                {bloqueado && (
                  <div style={{ padding:'8px 14px', background:'#D1FAE5', borderRadius:8, color:'#065F46', fontWeight:700, fontSize:13 }}>
                    ✅ Aprobado por {estados[tab]?.aprobado_por_nombre}
                  </div>
                )}
              </div>
            </div>

            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:800 }}>
                <thead>
                  <tr style={{ background:'#1a1a2e' }}>
                    <th style={{ padding:'10px 16px', textAlign:'left', color:'white', fontSize:13, fontWeight:700, width:170 }}>Empleado</th>
                    {DIAS_LABEL.map((dia, i) => (
                      <th key={dia} style={{ padding:'8px 4px', textAlign:'center', color:'white', fontSize:12, fontWeight:700, minWidth:100 }}>
                        <div>{dia}</div>
                        <div style={{ fontSize:10, opacity:0.65, fontWeight:400 }}>{format(new Date(fechasDias[i]+'T12:00'),'d MMM',{locale:es})}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {emps.length===0 ? (
                    <tr><td colSpan={9} style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>Sin empleados</td></tr>
                  ) : emps.map((emp, idx) => (
                    <tr key={emp.id} style={{ borderTop:'1px solid #F3F4F6', background:idx%2===0?'white':'#FAFAFA' }}>
                      <td style={{ padding:'8px 16px' }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{emp.nombre}</div>
                        <div style={{ fontSize:11, color:'#9CA3AF' }}>{emp.cargo||'—'}</div>
                      </td>
                      {DIAS.map((dia, di) => {
                        const tDia = turnosDeDia(tab, emp.id, dia);
                        const horasDia = tDia.reduce((a,t) => a+horasEntre(t.hora_inicio,t.hora_fin), 0);
                        return (
                          <td key={dia} style={{ padding:'5px 4px', textAlign:'center', verticalAlign:'top' }}>
                            <button
                              onClick={() => !bloqueado && abrirTurno(tab, emp.id, dia, DIAS_LABEL[di], format(new Date(fechasDias[di]+'T12:00'),'d/M/yyyy'))}
                              style={{
                                width:'100%', minHeight:54, border:`1px solid ${tDia.length?'#BBF7D0':'#E5E7EB'}`,
                                borderRadius:7, background:tDia.length?'#F0FDF4':'white',
                                cursor:bloqueado?'default':'pointer', padding:'4px 3px',
                                display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:1,
                              }}
                            >
                              {tDia.length===0 ? (
                                <span style={{ fontSize:10, color:'#D1D5DB' }}>{bloqueado?'—':'+turno'}</span>
                              ) : (
                                <>
                                  {tDia.map(t => (
                                    <div key={t.turno_num} style={{ fontSize:10, color:'#065F46', fontWeight:600, whiteSpace:'nowrap' }}>
                                      {t.hora_inicio.slice(0,5)}–{t.hora_fin.slice(0,5)}
                                    </div>
                                  ))}
                                  <div style={{ fontSize:10, fontWeight:800, color:'#10B981' }}>{horasDia}h</div>
                                </>
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                {/* Totales por día */}
                <tfoot>
                  <tr style={{ background:'#F9FAFB', borderTop:'2px solid #E5E7EB' }}>
                    <td style={{ padding:'10px 16px', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Total/día</td>
                    {DIAS.map((dia, i) => {
                      const total = emps.reduce((t, emp) => {
                        const p = progBodega[emp.id];
                        if (!p) return t;
                        return t + (turnos[p.id]||[]).filter(x=>x.dia===dia).reduce((a,x)=>a+horasEntre(x.hora_inicio,x.hora_fin),0);
                      }, 0);
                      return (
                        <td key={i} style={{ padding:'10px 4px', textAlign:'center', fontFamily:'monospace', fontWeight:800, fontSize:14, color:total>0?'#1a1a2e':'#D1D5DB' }}>
                          {total>0?`${Math.round(total*10)/10}h`:'—'}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Resumen consolidado — solo en editar */}
      {vista === 'editar' && (
      <div style={{ background:'white', borderRadius:16, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', fontWeight:800, fontSize:15 }}>
          📊 Resumen semanal por trabajador
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#F9FAFB' }}>
                <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Empleado</th>
                {DIAS_LABEL.map((d,i) => (
                  <th key={i} style={{ padding:'10px 4px', textAlign:'center', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', minWidth:72 }}>
                    {d}<br/><span style={{ fontWeight:400 }}>{format(new Date(fechasDias[i]+'T12:00'),'d MMM',{locale:es})}</span>
                  </th>
                ))}
                <th style={{ padding:'10px 12px', textAlign:'center', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Total</th>
                <th style={{ padding:'10px 12px', textAlign:'center', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Estado</th>
                <th style={{ padding:'10px 12px', width:80 }}></th>
              </tr>
            </thead>
            <tbody>
              {empleados.map((emp, idx) => {
                const horasDia = DIAS.map((_, i) => horasPorEmpleadoDia(emp.id, i));
                const total = horasDia.reduce((a,b)=>a+b,0);
                if (total===0) return null;
                const col = colorHoras(total);
                return (
                  <tr key={emp.id} style={{ borderTop:'1px solid #F3F4F6', background:idx%2===0?'white':'#FAFAFA' }}>
                    <td style={{ padding:'10px 16px' }}>
                      <div style={{ fontWeight:700 }}>{emp.nombre}</div>
                      <div style={{ fontSize:11, color:'#9CA3AF' }}>{emp.cargo||'—'}</div>
                    </td>
                    {horasDia.map((h, i) => {
                      const dia = DIAS[i];
                      const puntos = Object.entries(prog)
                        .filter(([bodId, filas]) => {
                          const p = filas[emp.id];
                          return p && (turnos[p.id]||[]).filter(t=>t.dia===dia).length>0;
                        })
                        .map(([bodId]) => bodegas.find(b=>String(b.id)===String(bodId))?.nombre||'?');
                      return (
                        <td key={i} style={{ padding:'8px 4px', textAlign:'center' }}>
                          {h>0 ? (
                            <div>
                              <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:13 }}>{h}h</div>
                              <div style={{ fontSize:9, color:'#9CA3AF' }}>{puntos.join(', ')}</div>
                            </div>
                          ) : <span style={{ color:'#E5E7EB' }}>—</span>}
                        </td>
                      );
                    })}
                    <td style={{ padding:'10px 12px', textAlign:'center', fontFamily:'monospace', fontWeight:800, fontSize:16, color:col.color }}>
                      {total.toFixed(1)}h
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center' }}>
                      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:col.bg, color:col.color }}>
                        {col.label}
                      </span>
                    </td>
                    <td style={{ padding:'10px 8px', textAlign:'center' }}>
                      <button
                        onClick={async () => {
                          setGenerandoPDF(emp.id);
                          try {
                            await generarPDFEmpleado(emp, semanaInicio, turnos, prog, bodegas);
                          } catch { toast.error('Error al generar PDF'); }
                          finally { setGenerandoPDF(null); }
                        }}
                        disabled={generandoPDF===emp.id}
                        style={{ padding:'5px 10px', border:'2px solid #E5E7EB', borderRadius:7, background:'white', cursor:'pointer', fontSize:11, fontWeight:700, color:'#374151' }}
                      >
                        {generandoPDF===emp.id ? '⏳' : '📄 PDF'}
                      </button>
                    </td>
                  </tr>
                );
              }).filter(Boolean)}
            </tbody>
          </table>
        </div>
        <div style={{ display:'flex', gap:16, padding:'12px 20px', borderTop:'1px solid #F3F4F6', flexWrap:'wrap', fontSize:12 }}>
          <span style={{ color:'#10B981', fontWeight:700 }}>● 42–44h: completo</span>
          <span style={{ color:'#EF4444', fontWeight:700 }}>● +44h: horas extras</span>
          <span style={{ color:'#F59E0B', fontWeight:700 }}>● -42h: incompleto</span>
        </div>
      </div>
      )} {/* fin vista editar */}

      {modalTurno && (
        <ModalTurnosDia {...modalTurno} onClose={() => setModalTurno(null)} onGuardado={cargar} />
      )}
      {modalRechazo && (
        <ModalRechazo bodegaId={modalRechazo} semanaInicio={semanaInicio} onClose={() => setModalRechazo(null)} onRechazado={cargar} />
      )}
    </div>
  );
}
